import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { FiatCurrency, InvoiceStatus, InvoiceSubStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, FILTER_FIELD_CLASSNAME, FilterField } from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { Amount, CryptoFiatAmount, formatCryptoAmount, formatFiatAmount, formatUsdAmount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { INVOICE_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { exportCsv } from "@/lib/csv";
import { formatStatusLabel } from "@/lib/utils";
import { getInvoiceAssetOptions, listInvoices } from "@/api/invoices";
import { listMerchants } from "@/api/merchants";
import { type InvoiceListItem, type ListInvoicesParams } from "@/api/types";

// Real implementation of Task 26 Part C: the invoices list, upgraded to the
// shared DataTable + `lib/table-state.ts`'s `useTableState` — the first
// Phase-4 list page to use it exactly as designed, because `GET /v1/invoices`
// genuinely supports server-side pagination/sort/filter (unlike Task 25's
// merchants list). URL-persisted filters/sort/page via `validateSearch`
// below. Row detail is now a real route (`/invoices/$invoiceId`, Part D)
// rather than the old in-page `InvoiceDetailDialog` — that dialog is removed
// entirely; its content moved to the new detail page.
// A `type` alias (not `interface`) — `useTableState<TFilters>`'s constraint
// is `Record<string, unknown>`, which only a type alias's implicit index
// signature satisfies; an interface with only named properties doesn't.
type InvoiceFilters = {
  status?: InvoiceStatus;
  subStatus?: InvoiceSubStatus;
  merchantId?: string;
  assetId?: string;
  fiatCurrency?: FiatCurrency;
  amountUsdMin?: string;
  amountUsdMax?: string;
  from?: string;
  to?: string;
};

const DEFAULT_FILTERS: InvoiceFilters = {};

/** Plain type-guarded passthrough — mirrors `_app.merchants.$merchantId.tsx`'s `validateSearch` house style. */
function validateInvoicesSearch(search: Record<string, unknown>): TableSearch<InvoiceFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as InvoiceStatus) : undefined,
    subStatus: typeof search.subStatus === "string" ? (search.subStatus as InvoiceSubStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    assetId: typeof search.assetId === "string" ? search.assetId : undefined,
    fiatCurrency: typeof search.fiatCurrency === "string" ? (search.fiatCurrency as FiatCurrency) : undefined,
    amountUsdMin: typeof search.amountUsdMin === "string" ? search.amountUsdMin : undefined,
    amountUsdMax: typeof search.amountUsdMax === "string" ? search.amountUsdMax : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  };
}

export const Route = createFileRoute("/_app/invoices")({
  validateSearch: validateInvoicesSearch,
  component: InvoicesPage,
});

// ─── formatting helpers (shared by table cells and CSV export) ─────────────

// Date + time, matching the merchant portal's invoices list — used to only
// show the date, but "Created" is also sortable and worth the extra
// precision.
function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

// ─── row → detail navigation ─────────────────────────────────────────────────
//
// DataTable has no onRowClick concept (out of scope to modify), so every
// cell's content is wrapped in a <Link> that fills the cell — same pattern
// Task 25's merchants list established.

function RowLink({ invoiceId, children }: { invoiceId: string; children: ReactNode }) {
  return (
    <Link to="/invoices/$invoiceId" params={{ invoiceId }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

// ─── invoices page ───────────────────────────────────────────────────────────

function InvoicesPage() {
  const tableState = useTableState<InvoiceFilters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });

  const params: ListInvoicesParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListInvoicesParams["sortBy"],
    sortOrder: tableState.sortOrder,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["invoices", params],
    queryFn: () => listInvoices(params),
  });

  // Merchant filter is a name dropdown (not a raw UUID input) — reuses Task
  // 14b's listMerchants rather than duplicating it.
  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) =>
    a.businessName.localeCompare(b.businessName),
  );

  // Task 26 Part A's new INVOICES_VIEWER-scoped read — the asset filter
  // dropdown, unlike `GET /v1/assets`, doesn't require CONFIG_MANAGER.
  const { data: assetOptions } = useQuery({
    queryKey: ["invoice-asset-options"],
    queryFn: getInvoiceAssetOptions,
  });

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<InvoiceListItem>[] = [
    {
      key: "publicId",
      header: "Invoice ID",
      cell: (inv) => <RowLink invoiceId={inv.id}><span className="group-hover:underline">{inv.publicId}</span></RowLink>,
    },
    {
      key: "merchant",
      header: "Merchant",
      filter: columnFilter(tableState, ["merchantId"], (draft, set) => (
        <FilterField id="filter-merchant" label="Merchant">
          <select
            id="filter-merchant"
            className={FILTER_FIELD_CLASSNAME}
            value={draft.merchantId ?? ""}
            onChange={(e) => set("merchantId", e.target.value || undefined)}
          >
            <option value="">All merchants</option>
            {sortedMerchants.map((m) => (
              <option key={m.id} value={m.id}>
                {m.businessName}
              </option>
            ))}
          </select>
        </FilterField>
      )),
      cell: (inv) => (
        <MerchantCell merchantId={inv.merchant.id} businessName={inv.merchant.businessName} publicId={inv.merchant.publicId} />
      ),
    },
    {
      key: "status",
      header: "Status",
      filter: columnFilter(tableState, ["status", "subStatus"], (draft, set) => (
        <>
          <FilterField id="filter-status" label="Status">
            <select
              id="filter-status"
              className={FILTER_FIELD_CLASSNAME}
              value={draft.status ?? ""}
              onChange={(e) => set("status", (e.target.value || undefined) as InvoiceStatus | undefined)}
            >
              <option value="">All statuses</option>
              {Object.values(InvoiceStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField id="filter-subStatus" label="Sub-status">
            <select
              id="filter-subStatus"
              className={FILTER_FIELD_CLASSNAME}
              value={draft.subStatus ?? ""}
              onChange={(e) => set("subStatus", (e.target.value || undefined) as InvoiceSubStatus | undefined)}
            >
              <option value="">All sub-statuses</option>
              {Object.values(InvoiceSubStatus).map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>
          </FilterField>
        </>
      )),
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          {/* subStatus, when present, only ever pairs with ACTION_REQUIRED
              and already fully explains the state — showing both would just
              repeat "Action required" next to its own reason. */}
          <StatusBadge
            label={formatStatusLabel(inv.subStatus ?? inv.status)}
            color={INVOICE_STATUS_COLOR[inv.status]}
          />
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      filter: columnFilter(tableState, ["assetId"], (draft, set) => (
        <FilterField id="filter-asset" label="Asset">
          <select
            id="filter-asset"
            className={FILTER_FIELD_CLASSNAME}
            value={draft.assetId ?? ""}
            onChange={(e) => set("assetId", e.target.value || undefined)}
          >
            <option value="">All assets</option>
            {(assetOptions ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.symbol} · {formatStatusLabel(a.network)}
              </option>
            ))}
          </select>
        </FilterField>
      )),
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          <AssetLabel symbol={inv.asset.symbol} network={inv.asset.network}/>
        </RowLink>
      ),
    },
    {
      key: "fiatAmount",
      header: "Paid",
      sortable: true,
      filter: columnFilter(tableState, ["fiatCurrency", "amountUsdMin", "amountUsdMax"], (draft, set) => (
        <>
          <FilterField id="filter-currency" label="Fiat currency">
            <select
              id="filter-currency"
              className={FILTER_FIELD_CLASSNAME}
              value={draft.fiatCurrency ?? ""}
              onChange={(e) => set("fiatCurrency", (e.target.value || undefined) as FiatCurrency | undefined)}
            >
              <option value="">All currencies</option>
              {Object.values(FiatCurrency).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </FilterField>
          <div className="grid grid-cols-2 gap-2">
            <FilterField id="filter-min" label="Min (USD)">
              <input
                id="filter-min"
                inputMode="decimal"
                placeholder="No minimum"
                className={FILTER_FIELD_CLASSNAME}
                value={draft.amountUsdMin ?? ""}
                onChange={(e) => set("amountUsdMin", e.target.value)}
              />
            </FilterField>
            <FilterField id="filter-max" label="Max (USD)">
              <input
                id="filter-max"
                inputMode="decimal"
                placeholder="No maximum"
                className={FILTER_FIELD_CLASSNAME}
                value={draft.amountUsdMax ?? ""}
                onChange={(e) => set("amountUsdMax", e.target.value)}
              />
            </FilterField>
          </div>
        </>
      )),
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          <CryptoFiatAmount
            native={inv.cryptoAmount}
            symbol={inv.asset.symbol}
            fiatAmount={inv.fiatAmount}
            fiatCurrency={inv.fiatCurrency}
          />
        </RowLink>
      ),
    },
    {
      key: "fee",
      header: "Fee",
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          <span className="block text-sm text-foreground">
            <Amount native={inv.cryptoFee} symbol={inv.asset.symbol} usd={inv.feeUsd}/>
          </span>
        </RowLink>
      ),
    },
    {
      key: "merchantAmount",
      header: "Settlement",
      cell: (inv) => (
        <RowLink invoiceId={inv.id}>
          <span className="block text-sm text-foreground">
            <Amount native={inv.merchantAmount} symbol={inv.asset.symbol} usd={inv.merchantAmountUsd}/>
          </span>
        </RowLink>
      ),
    },
    {
      key: "txHash",
      header: "Tx hash",
      // Deliberately NOT a RowLink here: an explorer link + copy button are
      // this cell's own interactive affordances, distinct from the row's
      // own detail-page destination — same convention as the merchant
      // portal's invoices list.
      cell: (inv) => <HashLink value={inv.txHash} type="tx" network={inv.asset.network} className="text-xs" />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      filter: columnFilter(tableState, ["from", "to"], (draft, set) => (
        <div className="grid grid-cols-2 gap-2">
          <FilterField id="filter-from" label="From">
            <input
              id="filter-from"
              type="date"
              className={FILTER_FIELD_CLASSNAME}
              value={draft.from ?? ""}
              onChange={(e) => set("from", e.target.value)}
            />
          </FilterField>
          <FilterField id="filter-to" label="To">
            <input
              id="filter-to"
              type="date"
              className={FILTER_FIELD_CLASSNAME}
              value={draft.to ?? ""}
              onChange={(e) => set("to", e.target.value)}
            />
          </FilterField>
        </div>
      )),
      cell: (inv) => <RowLink invoiceId={inv.id}>{formatDate(inv.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load invoices." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        csvExport={{
          onExport: () =>
            exportCsv({
              columns: [
                { header: "Invoice ID", value: (inv: InvoiceListItem) => inv.publicId },
                { header: "Merchant", value: (inv: InvoiceListItem) => inv.merchant.businessName },
                { header: "Asset", value: (inv: InvoiceListItem) => inv.asset.symbol },
                { header: "Network", value: (inv: InvoiceListItem) => inv.asset.network },
                { header: "Status", value: (inv: InvoiceListItem) => inv.status },
                { header: "Sub-status", value: (inv: InvoiceListItem) => inv.subStatus ?? "" },
                {
                  header: "Fiat amount",
                  value: (inv: InvoiceListItem) => formatFiatAmount(inv.fiatAmount, inv.fiatCurrency),
                },
                {
                  header: "Crypto amount",
                  value: (inv: InvoiceListItem) => formatCryptoAmount(inv.cryptoAmount, inv.asset.symbol),
                },
                {
                  header: "Fee",
                  value: (inv: InvoiceListItem) => formatCryptoAmount(inv.cryptoFee, inv.asset.symbol),
                },
                { header: "Fee (USD)", value: (inv: InvoiceListItem) => formatUsdAmount(inv.feeUsd) },
                {
                  header: "Settlement",
                  value: (inv: InvoiceListItem) => formatCryptoAmount(inv.merchantAmount, inv.asset.symbol),
                },
                {
                  header: "Settlement (USD)",
                  value: (inv: InvoiceListItem) => formatUsdAmount(inv.merchantAmountUsd),
                },
                { header: "Tx hash", value: (inv: InvoiceListItem) => inv.txHash ?? "" },
                { header: "Created", value: (inv: InvoiceListItem) => formatDate(inv.createdAt) },
              ],
              fetchPage: (page, pageSize) =>
                listInvoices({
                  ...tableState.filters,
                  sortBy: (tableState.sortBy ?? undefined) as ListInvoicesParams["sortBy"],
                  sortOrder: tableState.sortOrder,
                  page,
                  pageSize,
                }).then((r) => ({ data: r.data, total: r.total })),
              filenamePrefix: "invoices",
            }),
        }}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(inv) => inv.id}
        emptyMessage="No invoices match your filters."
      />
    </div>
  );
}
